import type { StateCreator, StoreApi } from 'zustand';
import type { ShellAdapter, SyncMessage } from './types';
import { IPC_SYNC_EVENTS } from './types';

export interface IpcSyncOptions<T> {
  /**
   * Unique channel name for this store. Stores on different windows
   * that share the same channel will synchronize.
   */
  channel: string;

  /**
   * Which top-level keys to synchronize. If omitted, ALL keys whose
   * values are not functions are synced. Explicitly listing keys is
   * recommended for clarity and performance.
   */
  syncKeys?: (keyof T & string)[];

  /**
   * Throttle interval in ms. Rapid `set` calls within this window
   * are batched into a single IPC message. Defaults to 0 (no throttle).
   */
  throttleMs?: number;

  /**
   * Override the origin identifier. Defaults to `shell.context` (e.g. 'main' | 'alert').
   */
  origin?: string;

  /**
   * Override the shell adapter. Defaults to `window.shell`.
   */
  adapter?: ShellAdapter;

  /**
   * Transform or reject an incoming patch before it's applied.
   * Return `false` to reject the entire patch.
   */
  onReceive?: (patch: Partial<T>, message: SyncMessage<Partial<T>>) => Partial<T> | false;

  /**
   * Transform or reject an outgoing patch before it's broadcast.
   * Return `false` to suppress the broadcast.
   */
  onSend?: (patch: Partial<T>) => Partial<T> | false;
}

function shallowDiff<T extends object>(prev: T, next: T, keys?: (keyof T & string)[]): Partial<T> | null {
  const patch = {} as Partial<T>;
  let hasChanges = false;

  const keysToCheck = (keys ?? Object.keys(next)) as (keyof T & string)[];

  for (const k of keysToCheck) {
    if (!Object.prototype.hasOwnProperty.call(next, k)) continue;
    if (typeof next[k] === 'function') continue;

    if (!Object.is(prev[k], next[k])) {
      patch[k] = next[k];
      hasChanges = true;
    }
  }

  return hasChanges ? patch : null;
}

function extractSyncedSlice<T extends object>(state: T, keys?: (keyof T & string)[]): Partial<T> {
  const slice = {} as Partial<T>;
  const keysToExtract = (keys ?? Object.keys(state)) as (keyof T & string)[];

  for (const k of keysToExtract) {
    if (!Object.prototype.hasOwnProperty.call(state, k)) continue;
    if (typeof state[k] === 'function') continue;
    slice[k] = state[k];
  }

  return slice;
}

type IpcSync = <T>(options: IpcSyncOptions<T>, initializer: StateCreator<T, [], []>) => StateCreator<T, [], []>;

const ipcSyncImpl: IpcSync =
  <T>(options: IpcSyncOptions<T>, initializer: StateCreator<T, [], []>): StateCreator<T, [], []> =>
  (set, get, api) => {
    const { channel, syncKeys, throttleMs = 0, onReceive, onSend } = options;

    type TSet = StoreApi<T>['setState'];
    type TGet = StoreApi<T>['getState'];

    const typedSet = set as TSet;
    const typedGet = get as TGet;

    let cachedAdapter: ShellAdapter | null = null;

    const getAdapter = (): ShellAdapter | null => {
      if (cachedAdapter) return cachedAdapter;
      const adapter =
        options.adapter ??
        (typeof window !== 'undefined' ? (window as unknown as { shell?: ShellAdapter }).shell ?? null : null);
      if (adapter) cachedAdapter = adapter;
      return adapter;
    };

    const getOrigin = (): string => {
      return options.origin ?? getAdapter()?.context ?? 'unknown';
    };

    let isRemoteUpdate = false;

    let pendingPatch: Partial<T> = {};
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;

    const broadcastPatch = (patch: Partial<T>) => {
      const adapter = getAdapter();
      if (!adapter) return;

      let outgoing = { ...patch };

      if (onSend) {
        const result = onSend(outgoing);
        if (result === false) return;
        outgoing = result;
      }

      const message: SyncMessage<Partial<T>> = {
        channel,
        patch: outgoing,
        origin: getOrigin(),
        timestamp: Date.now(),
      };

      adapter.emit(IPC_SYNC_EVENTS.BROADCAST, message);
    };

    const flushPatch = () => {
      if (Object.keys(pendingPatch).length === 0) return;

      const toSend = { ...pendingPatch };
      pendingPatch = {};
      broadcastPatch(toSend);
    };

    const scheduleBroadcast = (patch: Partial<T>) => {
      Object.assign(pendingPatch, patch);

      if (throttleMs > 0) {
        if (!throttleTimer) {
          throttleTimer = setTimeout(() => {
            throttleTimer = null;
            flushPatch();
          }, throttleMs);
        }
      } else {
        flushPatch();
      }
    };

    const diffAndBroadcast = (prevState: T, nextState: T) => {
      if (isRemoteUpdate) return;

      const diff = shallowDiff(prevState as T & object, nextState as T & object, syncKeys);

      if (diff) {
        scheduleBroadcast(diff);
      }
    };

    const syncSet: TSet = (...args: Parameters<TSet>) => {
      const prevState = typedGet();
      typedSet(...args);
      const nextState = typedGet();
      diffAndBroadcast(prevState, nextState);
    };

    const originalSetState = api.setState;
    api.setState = (...args: Parameters<TSet>) => {
      const prevState = api.getState();
      originalSetState(...args);
      const nextState = api.getState();
      diffAndBroadcast(prevState, nextState);
    };

    const applyRemotePatch = (patch: Partial<T>) => {
      const currentState = typedGet();
      const filtered = {} as Partial<T>;
      let hasRelevant = false;

      for (const key of Object.keys(patch) as (keyof T & string)[]) {
        if (!(key in (currentState as object))) continue;
        if (syncKeys && !syncKeys.includes(key)) continue;

        filtered[key] = patch[key];
        hasRelevant = true;
      }

      if (!hasRelevant) return;

      isRemoteUpdate = true;
      try {
        typedSet((state) => ({ ...state, ...filtered }), false);
      } finally {
        isRemoteUpdate = false;
      }
    };

    const setupListeners = () => {
      const adapter = getAdapter();
      if (!adapter) {
        queueMicrotask(() => {
          const retryAdapter = getAdapter();
          if (retryAdapter) {
            bindListeners(retryAdapter);
          } else {
            if (import.meta.env.MODE !== 'test') {
              console.debug(`[ipcSync:${channel}] No shell adapter found after retry. IPC sync disabled.`);
            }
          }
        });
        return;
      }
      bindListeners(adapter);
    };

    const bindListeners = (adapter: ShellAdapter) => {
      const listenerId = `ipc-sync-${channel}`;
      const updateEvent = IPC_SYNC_EVENTS.updateEvent(channel);
      const snapshotEvent = IPC_SYNC_EVENTS.snapshotEvent(channel);
      const peerRequestEvent = IPC_SYNC_EVENTS.peerRequestEvent(channel);

      adapter.on(
        updateEvent,
        (...args: unknown[]) => {
          const message = args[1] as SyncMessage<Partial<T>>;
          if (message.origin === getOrigin()) return;

          let patch = message.patch;
          if (onReceive) {
            const result = onReceive(patch, message);
            if (result === false) return;
            patch = result;
          }

          applyRemotePatch(patch);
        },
        listenerId
      );

      adapter.on(
        snapshotEvent,
        (...args: unknown[]) => {
          const message = args[1] as SyncMessage<Partial<T>>;
          if (message.origin === getOrigin()) return;
          applyRemotePatch(message.patch);
        },
        `${listenerId}-snapshot`
      );

      adapter.on(
        peerRequestEvent,
        () => {
          const currentState = typedGet();
          const slice = extractSyncedSlice(currentState as T & object, syncKeys);

          if (Object.keys(slice).length === 0) return;

          broadcastPatch(slice);
        },
        `${listenerId}-peer-request`
      );

      adapter.emit(IPC_SYNC_EVENTS.REQUEST, {
        channel,
        origin: getOrigin(),
      });

      const cleanup = () => {
        adapter.off(updateEvent, undefined, listenerId);
        adapter.off(snapshotEvent, undefined, `${listenerId}-snapshot`);
        adapter.off(peerRequestEvent, undefined, `${listenerId}-peer-request`);
        if (throttleTimer) {
          clearTimeout(throttleTimer);
          throttleTimer = null;
        }
      };

      (api as unknown as Record<string, unknown>).__ipcSyncCleanup = cleanup;
    };

    setupListeners();

    const initialState = initializer(syncSet as typeof set, get, api);

    queueMicrotask(() => {
      const adapter = getAdapter();
      if (!adapter) return;

      const currentState = typedGet();
      const slice = extractSyncedSlice(currentState as T & object, syncKeys);

      if (Object.keys(slice).length === 0) return;

      broadcastPatch(slice);
    });

    return initialState;
  };

/**
 * Zustand middleware that synchronizes selected state keys across
 * Electron renderer windows via IPC.
 *
 * @example
 * ```ts
 * import { create } from 'zustand';
 * import { ipcSync } from './zustand-ipc-sync';
 *
 * interface AppState {
 *   theme: 'light' | 'dark';
 *   locale: string;
 *   user: { name: string } | null;
 *   sidebarOpen: boolean;
 *   setTheme: (t: 'light' | 'dark') => void;
 * }
 *
 * const useAppStore = create<AppState>()(
 *   ipcSync(
 *     {
 *       channel: 'app',
 *       syncKeys: ['theme', 'locale', 'user'],
 *       throttleMs: 50,
 *     },
 *     (set) => ({
 *       theme: 'light',
 *       locale: 'en',
 *       user: null,
 *       sidebarOpen: false,
 *       setTheme: (theme) => set({ theme }),
 *     }),
 *   ),
 * );
 * ```
 */
export const ipcSync = ipcSyncImpl;

export function cleanupIpcSync(api: StoreApi<unknown>): void {
  const cleanup = (api as unknown as Record<string, unknown>).__ipcSyncCleanup;
  if (typeof cleanup === 'function') cleanup();
}
